import { IStore } from 'Store/.';
import { createSelector } from 'reselect';
import { getNumberOfManualReservations } from 'Components/PickSeatManually/Redux/selectors';

const getOrderRequest = (state: IStore) => state.order.orderRequest;
const getIsManualReservation = (state: IStore) => state.order.orderRequest.isManualReservation;
const getSelectedSeatRequests = (state: IStore) => state.order.orderRequest.seatRequests;
const getSelectedScatteredSeats = (state: IStore) => state.order.orderRequest.isScatteredSeats;
const getSelectedPackages = (state: IStore) => state.order.selectedPackages;
const getSelectedDeliveryCode = (state: IStore) => state.order.selectedDeliveryCode;
const getSelectedPaymentName = (state: IStore) => state.order.selectedPaymentCode;
const getSelectedCustomerInfo = (state: IStore) => state.order.selectedCustomerInfo;
const getSelectedSkanetrafiken = (store: IStore) => store.order.selectedSkanetrafiken;
const getSelectedVouchers = (store: IStore) => store.order.selectedVouchers;
const getIsTermsApproved = (state: IStore) => state.order.isTermsApproved;
const getPackages = (state: IStore) => state.order.packages;
const getDeliveryMethods = (state: IStore) => state.order.deliveryMethods;
const getPaymentMethods = (state: IStore) => state.order.paymentMethods;
const getSkanetrafiken = (state: IStore) => state.order.skanetrafiken;
const getOrderID = (state: IStore) => state.order.orderID;
const getOrderBarcode = (state: IStore) => state.order.orderBarcode;
const getOrderExpireDate = (state: IStore) => state.order.orderExpireDate;
const getOrderSeats = (state: IStore) => state.order.orderSeats;
const getOrderVouchers = (state: IStore) => state.order.orderVouchers;
const getOrderItems = (state: IStore) => state.order.orderItems;
const getOrderTotalPrice = (state: IStore) => state.order.orderTotalPrice;
const getPaymentStatus = (state: IStore) => state.order.paymentStatus;
const getPaymentReturnUrl = (state: IStore) => state.order.paymentUrl;
const getIsFetchingOrder = (state: IStore) => state.order.isFetchingOrder;
const getIsFetchOrderFailed = (state: IStore) => state.order.isFetchOrderFailed;
const getIsFetchingPayment = (state: IStore) => state.order.isFetchingPayment;
const getIsFetchPaymentFailed = (state: IStore) => state.order.isFetchPaymentFailed;
const getIsFetchingDiscount = (state: IStore) => state.order.isFetchingDiscount;
const getIsDiscountFailed = (state: IStore) => state.order.isDiscountFailed;
const getIsRetry = (state: IStore) => state.order.isRetry;
const getOrderIsValid = (state: IStore) => state.order.isValid;
const getLocale = (state: IStore) => state.user.locale;

const getIsOrderSeatsEmpty = createSelector(
  [getOrderSeats],
  (orderSeats) => {
    return !orderSeats || orderSeats.length <= 0;
  },
);

const getIsOrderVouchersEmpty = createSelector(
  [getOrderVouchers],
  (orderVouchers) => {
    return !orderVouchers || orderVouchers.length <= 0;
  },
);

const getIsOrderItemsEmpty = createSelector(
  [getOrderItems],
  (orderItems) => {
    return !orderItems || orderItems.length <= 0;
  },
);

const getVoucherRequests = createSelector(
  [getOrderRequest],
  (orderRequest) => {
    return orderRequest && orderRequest.voucherRequests ? orderRequest.voucherRequests : [];
  },
);

const getSeatsTotalPrice = createSelector(
  [getOrderSeats],
  (orderSeats) => {
    return orderSeats ? orderSeats.reduce(
      (price, seat) => seat.price === seat.reducedPrice ? 
      price + seat.price : price + seat.reducedPrice, 0) : 0;
  },
);

const getSelectedPackagesSum = createSelector(
  [getSelectedPackages],
  (packages) => {
    return packages ? packages.reduce((price, request) => 
      price + request.price * request.numberOfPackages, 0) : 0;
  },
);

const getIsDeliverySelected = createSelector(
  [getSelectedDeliveryCode],
  (deliveryCode) => {
    return deliveryCode !== null;
  },
);

const getIsCustomerSelected = createSelector(
  [getSelectedCustomerInfo],
  (customerInfo) => {
    return customerInfo !== null;
  },
);

const getIsPaymentSelected = createSelector(
  [getSelectedPaymentName],
  (paymentMethodID) => {
    return paymentMethodID !== null;
  },
);

const getOrderDetails = createSelector(
  [getSelectedCustomerInfo, getSelectedPackages, getSelectedDeliveryCode, getLocale],
  (selectedCustomerInfo, selectedPackages, selectedDeliveryCode, locale) => {
    return selectedCustomerInfo && selectedDeliveryCode ? 
      { 
        customerInfo: selectedCustomerInfo, 
        packageRequests: selectedPackages, 
        deliveryCode: selectedDeliveryCode,
        locale: locale
      } : null;
  },
);

const getCustomerEmail = createSelector(
  [getSelectedCustomerInfo],
  (customerInfo) => {
    return customerInfo && customerInfo.email && customerInfo.email.length ? customerInfo.email : null;
  },
);

const getNumberOfOrderSeats = createSelector(
  [getOrderSeats],
  (seatReservations) => {
    return seatReservations ? seatReservations.length : 0;
  },
);

const getNumberOfOrderItems = createSelector(
  [getOrderItems],
  (items) => {
    return items ? items.length : 0;
  },
);

const getNumberOfOrderVouchers = createSelector(
  [getOrderVouchers],
  (orderVouchers) => {
    return orderVouchers ? orderVouchers.length : 0;
  },
);

const getOrderContentCount = createSelector(
  [getNumberOfOrderSeats, getNumberOfOrderVouchers, getNumberOfManualReservations],
  (nbrOfSeats, nbrOfVouchers, nbrOfManualSeats) => {
    return nbrOfSeats + nbrOfVouchers > 0 ? nbrOfSeats + nbrOfVouchers :
      nbrOfManualSeats > 0 ? nbrOfManualSeats : 0;
  },
);

const getIsMaxGoThereTravels = createSelector(
  [getSelectedSkanetrafiken, getNumberOfOrderSeats],
  (travels, numberOfSeats) => {
    if (numberOfSeats <= 0) {
      return true;
    }
    if (!travels || !travels.length) {
      return false;
    }
    return travels.filter(t => t.direction === 0).length >= numberOfSeats;
  },
);

const getIsMaxGoHomeTravels = createSelector(
  [getSelectedSkanetrafiken, getNumberOfOrderSeats],
  (travels, numberOfSeats) => {
    if (numberOfSeats <= 0) {
      return true;
    }
    if (!travels || !travels.length) {
      return false;
    }
    return travels.filter(t => t.direction === 1).length >= numberOfSeats;
  },
);

const getNumberOfSkanetrafikenTickets = createSelector(
  [getSelectedSkanetrafiken],
  (requests) => {
    return requests ? requests.length : 0;
  },
);

const getIsMaxNbrOfSkanetrafikenBooked = createSelector(
  [getNumberOfOrderSeats, getNumberOfSkanetrafikenTickets],
  (nbrOfSeats, nbrOfRequests) => {
    return nbrOfRequests >= nbrOfSeats * 2;
  },
);

const getIsFetchingOrderOrPayment = createSelector(
  [getIsFetchingOrder, getIsFetchingPayment],
  (isFetchingOrder, isFetchingPayment) => {
    return isFetchingOrder || isFetchingPayment;
  },
);

const getIsFetchOrderOrPaymentFailed = createSelector(
  [getIsFetchOrderFailed, getIsFetchPaymentFailed],
  (isOrderFailed, isPaymentFailed) => {
    return isOrderFailed || isPaymentFailed;
  },
);

const getIsSkanetrafikenBooked = createSelector(
  [getSelectedSkanetrafiken],
  (skanetrafiken) => {
    return skanetrafiken && skanetrafiken.length > 0;
  },
);

export {
  getOrderID,
  getOrderBarcode,
  getOrderExpireDate,
  getOrderRequest,
  getOrderSeats,
  getOrderItems,
  getOrderVouchers,
  getOrderTotalPrice,
  getOrderDetails,
  getOrderContentCount,
  getVoucherRequests,
  getSeatsTotalPrice,
  getPaymentReturnUrl,
  getPaymentStatus,
  getSelectedVouchers,
  getSelectedSeatRequests,
  getSelectedScatteredSeats,
  getSelectedPackages,
  getSelectedSkanetrafiken,
  getSelectedDeliveryCode,
  getSelectedPaymentName,
  getSelectedCustomerInfo,
  getSelectedPackagesSum,
  getPackages,
  getDeliveryMethods,
  getPaymentMethods,
  getSkanetrafiken,
  getCustomerEmail,
  getIsTermsApproved,
  getIsDeliverySelected,
  getIsCustomerSelected,
  getIsPaymentSelected,
  getIsDiscountFailed,
  getIsManualReservation,
  getIsMaxGoThereTravels,
  getIsMaxGoHomeTravels,
  getIsMaxNbrOfSkanetrafikenBooked,
  getIsSkanetrafikenBooked,
  getIsRetry,
  getIsOrderItemsEmpty,
  getIsOrderSeatsEmpty,
  getIsOrderVouchersEmpty,
  getIsFetchingOrder,
  getIsFetchingPayment,
  getIsFetchingOrderOrPayment,
  getIsFetchingDiscount,
  getIsFetchOrderFailed,
  getIsFetchPaymentFailed,
  getIsFetchOrderOrPaymentFailed,
  getNumberOfOrderSeats,
  getNumberOfOrderVouchers,
  getOrderIsValid,
};


// WEBPACK FOOTER //
// ./src/store/Order/selectors.tsx